import React from 'react';
import { Typography, Box, IconButton, Tooltip, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export type NetworkType = 'stellar' | 'sui' | null;

interface BalanceInfo {
  symbol: string;
  balance: string | number;
  color: string;
}

interface DePINHeaderProps {
  // Page info
  title: string;
  subtitle: React.ReactNode;
  sectionTitle: string;
  sectionSubtitle: string;
  network: NetworkType;
  
  // Wallet status
  isConnected: boolean;
  balances?: BalanceInfo[];
  
  // Search
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  
  // Actions
  loading?: boolean;
  onRefresh?: () => void;
  onAdd?: () => void;
  onFilter?: () => void;
  showAddButton?: boolean;
  showFilterButton?: boolean;
  
  // Back navigation (for multi-step flows like add bucket)
  showBackButton?: boolean;
  onBack?: () => void;
  backButtonText?: string;
}

const DePINHeader: React.FC<DePINHeaderProps> = ({
  title,
  subtitle,
  sectionTitle,
  sectionSubtitle,
  network,
  isConnected,
  balances = [],
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  loading = false,
  onRefresh,
  onAdd,
  onFilter,
  showAddButton = true,
  showFilterButton = false,
  showBackButton = false,
  onBack,
  backButtonText = 'Return',
}) => {
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Just use the title without network label
  const fullTitle = title;

  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>
      {/* Main Header */}
      <div style={{ padding: '24px 40px 24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1F2937',
              marginBottom: '8px',
              fontFamily: 'Inter',
              letterSpacing: '-0.02em',
            }}
          >
            {showBackButton ? sectionTitle : fullTitle}
          </Typography>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#6B7280',
              fontFamily: 'Inter',
              lineHeight: '24px',
            }}
          >
            {showBackButton ? sectionSubtitle : subtitle}
          </Typography>
        </div>
        
        {showBackButton && onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
            style={{ border: '1px solid #D1D5DB' }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            {backButtonText}
          </button>
        )}
      </div>

      {/* Sub-section with buttons */}
      {!showBackButton && (
        <div className="px-10 pb-4">
          <Box sx={{ ml: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                  {sectionTitle}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                  {sectionSubtitle}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Wallet Status */}
                {isConnected ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-green-200 bg-green-50 mr-2">
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#10B981' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#10B981' }}>
                      Connected
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 mr-2">
                    <ErrorIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#DC2626' }}>
                      Not Connected
                    </span>
                  </div>
                )}
                
                {/* Balance badges */}
                {isConnected && balances.map((bal, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white">
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: bal.color }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#414651' }}>
                      {typeof bal.balance === 'number' ? bal.balance.toLocaleString() : bal.balance} {bal.symbol}
                    </span>
                  </div>
                ))}

                {/* Divider */}
                {(balances.length > 0 || isConnected) && (
                  <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB', marginLeft: 8, marginRight: 8 }} />
                )}
                
                {/* Search */}
                {searchOpen || showSearch ? (
                  <TextField
                    size="small"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    autoFocus
                    sx={{ 
                      width: 200, 
                      '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } 
                    }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                    }}
                  />
                ) : (
                  <Tooltip title="Search">
                    <IconButton
                      size="small"
                      onClick={() => setSearchOpen(true)}
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        border: '1px solid #D0D5DD', 
                        borderRadius: '8px', 
                        color: '#667085', 
                        '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } 
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {/* Reload */}
                <Tooltip title="Refresh">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onRefresh}
                      disabled={loading}
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        border: '1px solid #D0D5DD', 
                        borderRadius: '8px', 
                        color: '#667085', 
                        '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' },
                        '&.Mui-disabled': { opacity: 0.5 }
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 18, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                  </span>
                </Tooltip>
                
                {/* Filter */}
                {showFilterButton && (
                  <Tooltip title="Filter">
                    <IconButton
                      size="small"
                      onClick={onFilter}
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        border: '1px solid #D0D5DD', 
                        borderRadius: '8px', 
                        color: '#667085', 
                        '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } 
                      }}
                    >
                      <FilterListIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {/* Add */}
                {showAddButton && onAdd && (
                  <Tooltip title="Add">
                    <IconButton
                      size="small"
                      onClick={onAdd}
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        bgcolor: '#617BFF', 
                        borderRadius: '8px', 
                        color: 'white', 
                        '&:hover': { bgcolor: '#4A67E3' } 
                      }}
                    >
                      <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
          
          <div className="border-b border-gray-200"></div>
        </div>
      )}
    </div>
  );
};

export default DePINHeader;

